/**
 * Currency configuration types for trade booking
 */

export interface Currency {
  code: string;           // ISO 4217 code (e.g., "USD", "CLP")
  name: string;           // Full name (e.g., "United States Dollar")
  symbol: string;         // Currency symbol (e.g., "$", "€")
  enabled: boolean;       // Whether this currency is available for selection
  decimals: number;       // Number of decimal places (typically 2, but some currencies differ)
}

/**
 * Mock currency data
 * In Phase 2, this will be loaded from Firestore currencies collection
 */
export const MOCK_CURRENCIES: Currency[] = [
  { code: 'USD', name: 'United States Dollar', symbol: '$', enabled: true, decimals: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', enabled: true, decimals: 2 },
  { code: 'CLF', name: 'Unidad de Fomento', symbol: 'UF', enabled: true, decimals: 4 },
  { code: 'GBP', name: 'British Pound Sterling', symbol: '£', enabled: true, decimals: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', enabled: true, decimals: 2 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', enabled: true, decimals: 0 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', enabled: true, decimals: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', enabled: true, decimals: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', enabled: true, decimals: 2 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', enabled: true, decimals: 2 },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', enabled: true, decimals: 2 },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', enabled: true, decimals: 2 },
  { code: 'COP', name: 'Colombian Peso', symbol: 'COL$', enabled: true, decimals: 2 },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$', enabled: true, decimals: 2 },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$', enabled: true, decimals: 0 },
];

/**
 * Valid currency pair map — for each Currency 1, which Currency 2 options are available.
 * Based on standard market conventions for Chilean corporate FX.
 */
const CURRENCY_PAIR_MAP: Record<string, string[]> = {
  USD: ['CLP', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'BRL', 'MXN', 'PEN', 'COP', 'ARS'],
  EUR: ['CLP', 'USD', 'GBP', 'JPY', 'CHF'],
  GBP: ['CLP', 'USD', 'EUR', 'JPY', 'CHF'],
  JPY: ['CLP'],
  CHF: ['CLP'],
  CAD: ['CLP'],
  AUD: ['CLP'],
  CNY: ['CLP'],
  BRL: ['CLP'],
  MXN: ['CLP'],
  PEN: ['CLP'],
  COP: ['CLP'],
  ARS: ['CLP'],
  CLF: ['CLP'], // CLF (UF) only pairs with CLP — Forward de Inflación
  CLP: [], // CLP is not typically Currency 1
};

/**
 * Get list of enabled currencies
 */
export const getEnabledCurrencies = (): Currency[] => {
  return MOCK_CURRENCIES.filter(c => c.enabled);
};

/**
 * Get valid Currency 2 options for a given Currency 1.
 * Returns only enabled currencies that form a valid market pair.
 * If currency1 is empty/null, returns all enabled currencies.
 */
export const getValidCurrency2Options = (currency1: string | null | undefined): Currency[] => {
  if (!currency1) return getEnabledCurrencies();
  const validCodes = CURRENCY_PAIR_MAP[currency1];
  if (!validCodes || validCodes.length === 0) return [];
  const options = MOCK_CURRENCIES.filter(c => c.enabled && validCodes.includes(c.code));
  const clp = options.find(c => c.code === 'CLP');
  const rest = options.filter(c => c.code !== 'CLP');
  return clp ? [clp, ...rest] : options;
};

/**
 * Get valid Currency 1 options for a given trade type.
 * - CLP is never a valid Currency 1.
 * - CLF (Unidad de Fomento) is only valid for Forward trades — it is not physically deliverable.
 */
export const getCurrency1Options = (tradeType: string): Currency[] => {
  return MOCK_CURRENCIES.filter(c => {
    if (!c.enabled) return false;
    if (c.code === 'CLP') return false;                          // CLP is never Currency 1
    if (c.code === 'CLF' && tradeType !== 'FORWARD') return false; // CLF only for Forwards
    return true;
  });
};

/**
 * Get settlement currency options, with CLP sorted first.
 * CLF is excluded as it cannot be a settlement currency.
 */
export const getSettlementCurrencyOptions = (): Currency[] => {
  const enabled = MOCK_CURRENCIES.filter(c => c.enabled && c.code !== 'CLF');
  const clp = enabled.find(c => c.code === 'CLP');
  const rest = enabled.filter(c => c.code !== 'CLP');
  return clp ? [clp, ...rest] : rest;
};

/**
 * Get currency by code
 */
export const getCurrencyByCode = (code: string): Currency | undefined => {
  return MOCK_CURRENCIES.find(c => c.code === code);
};
